import { createEffect, createMemo } from "solid-js";
import { isNil } from "lodash-es";
import ApiService from "../../../../services/ApiService.ts";
import { useLocalUserSettings, useLocalUserSettingsDispatch } from "../../../../state/localUserSettings.tsx";
import type CustomerId from "../../../../state/CustomerId.ts";

export const useLastEditedForms = (customerId: CustomerId) => {
  const localUserSettings = useLocalUserSettings();
  const setLocalUserSettings = useLocalUserSettingsDispatch();
  const folderQuery = ApiService.getFolders();

  const formAndFolderById = createMemo(() =>
    Object.fromEntries(
      folderQuery.data?.flatMap((folder) =>
        folder.forms.map((form) => [form.id.toString(), { folder, form }] as const),
      ) ?? [],
    ),
  );

  const lastEditedFormIds = createMemo(() => localUserSettings.lastEditedForms?.[customerId.toString()] ?? []);

  const activeLastEditedForms = createMemo(() =>
    lastEditedFormIds().flatMap((formId) => {
      // Filter out deleted, permission revoked, and non 'ACTIVE' forms
      const entry = formAndFolderById()[formId.toString()];
      return isNil(entry) || entry.form.status !== "ACTIVE" ? [] : [{ formId, ...entry }];
    }),
  );

  createEffect(() => {
    if (isNil(folderQuery.data) || activeLastEditedForms().length === lastEditedFormIds().length) {
      return;
    }

    const formIds = activeLastEditedForms().map(({ formId }) => formId);

    setLocalUserSettings("lastEditedForms", (current) => ({
      ...current,
      [customerId.toString()]: formIds,
    }));
  });

  return activeLastEditedForms;
};
